import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Button, Grid } from "@mui/material";

import {
  campaignFormatHelper,
  getCampaignStatusText,
  getFormatDate,
  getStatusCode,
} from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import LearnMoreButtonBase from "@/components/muiCustom/buttons/LearnMoreButton";

import { PauseLocationAddIcon as PauseLocationAddIconBase } from "@/features/campaigns/digital/pause-campaign";
import LocationListModal from "@/components/modals/LocationListModal";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { CAMPAIGN_CONST, USER_ROLE } from "@/config/constants";

const RouterButton = Button as React.ComponentType<any>;
const PauseLocationAddIcon =
  PauseLocationAddIconBase as React.ComponentType<any>;
const LearnMoreButton = LearnMoreButtonBase as React.ComponentType<any>;

type JoinedCampaign = {
  id?: string | number;
  status?: string | number;
  participant_campaign?: {
    is_draft?: boolean;
    locationData?: any[];
    start_date?: string | null;
    end_date?: string | null;
  } | null;
  budget?: string | number | null;
  brand_contribution?: boolean | string | number | null;
  total_of_budget_and_brand_match?: string | number | null;
  is_campaign_boost?: boolean;
  can_participant_extend_campaign?: boolean;
  pause_campaign_available?: boolean;
  [key: string]: any;
};

type JoinedCampaignCardProps = {
  campaign?: JoinedCampaign | null;
  isHideBudget?: boolean;
  showActionsColumn?: boolean;
};

export default function JoinedCampaignCard({
  campaign,
  isHideBudget,
  showActionsColumn = true,
}: JoinedCampaignCardProps) {
  const {
    id,
    status,
    participant_campaign,
    budget,
    brand_contribution,
    total_of_budget_and_brand_match,
  } = campaign || {};
  const { selectedBrand, loggedInUserRole } = useContext(GlobalContext);
  const renderLocations = () => {
    if (participant_campaign && participant_campaign.is_draft === false) {
      const locs = participant_campaign.locationData || [];
      return locs.length ? (
        <>
          <LocationListModal locations={locs} />
        </>
      ) : (
        0
      );
    }
    return null;
  };
  const hasBoost =
    !!campaign?.is_campaign_boost &&
    selectedBrand &&
    selectedBrand?.status &&
    (campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
      campaign?.status === CAMPAIGN_CONST.STATUS.SCHEDULED.ID);

  const hasExtend =
    campaign?.can_participant_extend_campaign &&
    campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID;

  const hasPause =
    campaign &&
    selectedBrand &&
    selectedBrand?.status &&
    campaign.pause_campaign_available;

  const hasAnyAction = hasBoost || hasExtend || hasPause;

  const renderActions = () => {
    const buttonStyle = {
      alignItems: "center",
      display: "inline-flex",
      fontSize: "0.7rem",
      justifyContent: "center",
      lineHeight: 1,
      padding: "4px 18px",
      minWidth: "auto",
      height: "24px",
      borderRadius: "var(--radius-control)",
      flexShrink: 0,
      boxShadow: "none",
      border: "none",
    };

    return (
      <div
        className="d-flex align-items-center flex-wrap"
        style={{ gap: "8px" }}
      >
        {hasBoost ? (
          <RouterButton
            component={Link}
            to={{
              pathname: `/adjust-budget/${id}`,
              state: { campaignId: id, from: "/dashboard" },
            }}
            size="small"
            variant="contained"
            color="warning"
            className="text-white fw-sb px-4"
            style={{ ...buttonStyle, backgroundColor: "var(--status-warning)" }}
          >
            Boost
          </RouterButton>
        ) : null}
        {hasExtend ? (
          <RouterButton
            component={Link}
            to={`/extend-campaign/${id}`}
            size="small"
            variant="contained"
            color="warning"
            className="text-white fw-sb"
            style={{ ...buttonStyle, backgroundColor: "var(--primary-base)" }}
          >
            Extend
          </RouterButton>
        ) : null}
        {hasPause ? (
          <PauseLocationAddIcon
            campaignId={id}
            campaign={campaign}
            style={{ width: "24px", height: "24px" }}
            popoverPosition="bottom-left"
          />
        ) : null}
      </div>
    );
  };
  const renderBudget = () => {
    let campaignBudget = budget || 0;
    if (brand_contribution) {
      campaignBudget = total_of_budget_and_brand_match || 0;
    }
    // if (
    //   local_selection !== 0 &&
    //   participant_campaign &&
    //   participant_campaign.is_draft === false &&
    //   participant_campaign.total_budget
    // ) {
    //   campaignBudget = participant_campaign.total_budget;

    //   if (brand_contribution) {
    //     campaignBudget = total_of_budget_and_brand_match;
    //   }
    // }

    return <CurrencyFormateText value={campaignBudget} />;
  };

  return (
    <div className="card h-100">
      <div className="card-body p-3">
        <div className="row">
          <div className="col-7 col-lg-2 fw-sb">
            {campaignFormatHelper(campaign).name()}
          </div>
          <div className="col-5 col-lg text-right text-lg-left pl-lg-0">
            <StatusChip
              label={getCampaignStatusText(status)}
              status={getStatusCode(status, "hold")}
              showOnlyLabel={true}
            />
          </div>
          {showActionsColumn && (
            <div className="col-12 col-lg-2 mt-3 mt-lg-0 px-lg-0 mr-lg-0 mr-3">
              {hasAnyAction ? (
                <div className="d-lg-none title mb-1">Actions: </div>
              ) : null}
              <Grid
                container
                alignItems="center"
                justifyContent={{
                  md: "flex-start",
                }}
              >
                {renderActions()}
              </Grid>
            </div>
          )}
          {!isHideBudget && (
            <div
              className={`col-6 col-lg-1 mt-2 mt-lg-0 ${showActionsColumn ? "col-lg-1" : "col-lg-2"}`}
            >
              <div className="d-lg-none title">Active Budget: </div>
              <span className="title-val fw-b">{renderBudget()}</span>
            </div>
          )}
          <div
            className={`col-${isHideBudget ? 12 : 6} col-lg-1 mt-2 mt-lg-0 ${showActionsColumn ? "col-lg-1" : "col-lg-2"}`}
          >
            <div className="d-lg-none title">Enrolled Locations: </div>
            <span className="title-val fw-b">{renderLocations()}</span>
          </div>
          {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME ? (
            <div className="col-6 col-lg mt-2 mt-lg-0">
              <div className="d-lg-none title">Dates: </div>
              <span className="title-val fw-b">
                {getFormatDate(
                  campaign?.participant_campaign?.start_date,
                  "MMM DD, YYYY"
                )}{" "}
                -{" "}
                {getFormatDate(
                  campaign?.participant_campaign?.end_date,
                  "MMM DD, YYYY"
                )}
              </span>
            </div>
          ) : (
            <>
              <div className="col-6 col-lg mt-2 mt-lg-0">
                <div className="d-lg-none title">Start Date: </div>
                <span className="title-val">
                  {campaignFormatHelper(campaign).startDate("MMM DD, YYYY")}
                </span>
              </div>
              <div className="col-6 col-lg mt-2 mt-lg-0">
                <div className="d-lg-none title">End Date: </div>
                <span className="title-val">
                  {campaignFormatHelper(campaign).endDate("MMM DD, YYYY")}
                </span>
              </div>
            </>
          )}

          <div
            className="col-6 col-lg mt-3 mt-lg-0"
            style={{ height: "fit-content" }}
          >
            <div className="d-lg-none title mr-2 mb-1">Channels: </div>
            <div className="title-val">
              {campaignFormatHelper(campaign).getChannelIcons()}
            </div>
          </div>
          <div className="col-6 col-lg-1 mt-2 mt-lg-0">
            <div className="d-lg-none title">FAQ: </div>
            <span className="title-val">
              <Grid size={{ lg: "auto" }}>
                <LearnMoreButton
                  to={{
                    pathname: `/dashboard/learn-more/digital-campaign/${id}`,
                    state: { campaignId: id, from: "/dashboard" },
                  }}
                  icon
                  style={{ width: "24px", height: "24px" }}
                />
              </Grid>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
